import React from 'react'
import { Bar } from 'react-chartjs-2'
import {
  Chart as ChartJS,
  BarElement,
  CategoryScale,
  LinearScale,
  Tooltip,
  Legend,
} from 'chart.js'

const PayrollChart = ({ data }) => {
  ChartJS.register(BarElement, CategoryScale, LinearScale, Tooltip, Legend)

  // Calculate max total and appropriate step size
  const calculateChartScale = (chartData) => {
    if (!chartData?.datasets?.length || !chartData?.labels?.length) {
      return { max: 0, stepSize: 100000 }
    }

    let maxTotal = 0

    // Calculate the maximum total across all months (sum of all datasets for each label)
    chartData.labels.forEach((_, index) => {
      let total = 0
      chartData.datasets.forEach((dataset) => {
        // Convert string values to numbers and handle null/undefined
        const value = dataset.data[index]
        const numericValue =
          typeof value === 'string'
            ? parseFloat(value.replace(/,/g, '')) || 0
            : Number(value) || 0
        total += numericValue
      })
      if (total > maxTotal) maxTotal = total
    })

    // Set appropriate step size based on the max value
    let stepSize
    if (maxTotal >= 10000000) {
      // 10M+
      stepSize = 2000000 // 2M steps
    } else if (maxTotal >= 1000000) {
      // 1M+
      stepSize = 500000 // 0.5M steps
    } else if (maxTotal >= 500000) {
      // 500K+
      stepSize = 100000 // 100K steps
    } else if (maxTotal >= 100000) {
      // 100K+
      stepSize = 50000 // 50K steps
    } else if (maxTotal >= 50000) {
      // 50K+
      stepSize = 25000 // 25K steps
    } else {
      stepSize = 10000 // 10K steps
    }

    return {
      max: Math.ceil(maxTotal * 1.1), // 10% padding
      stepSize: stepSize,
    }
  }

  // Process data to ensure all values are numbers
  const processChartData = (rawData) => {
    if (!rawData?.data) return { labels: [], datasets: [] }

    const processedDatasets = rawData.data.datasets.map((dataset) => ({
      ...dataset,
      data: dataset.data.map((value) =>
        typeof value === 'string'
          ? parseFloat(value.replace(/,/g, '')) || 0
          : Number(value) || 0
      ),
      borderRadius: {
        topLeft: 6,
        topRight: 6,
      },
      barThickness: 20,
    }))

    return {
      ...rawData.data,
      datasets: processedDatasets,
    }
  }

  const processedChartData = processChartData(data)
  const { max, stepSize } = calculateChartScale(processedChartData)

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    barThickness: 20,
    plugins: {
      legend: {
        position: 'bottom',
        align: 'end',

        labels: {
          usePointStyle: true,
          pointStyle: 'circle',
          boxWidth: 10,
          padding: 15,
        },
      },
      tooltip: {
        backgroundColor: '#f4f4f4',
        titleColor: '#000',
        bodyColor: '#000',
        borderColor: '#ccc',
        borderWidth: 1,
        padding: 10,
        callbacks: {
          label: function (context) {
            const value = context.raw || 0
            const label = context.dataset.label
            // Show formatted amount in tooltip
            const formatted = `₦${
              value?.toLocaleString('en-US', {
                minimumFractionDigits: 2,
                maximumFractionDigits: 2,
              }) || '0.00'
            }`
            return `${label}: ${formatted}`
          },
        },
      },
    },
    scales: {
      x: {
        stacked: true,
        grid: {
          display: false,
        },
      },
      y: {
        stacked: true,
        beginAtZero: true,
        max: max,
        ticks: {
          stepSize: stepSize,
          callback: function (value) {
            if (value === 0) return '₦0'
            if (value >= 1000000) {
              return `₦${(value / 1000000).toFixed(1)}M`
            } else if (value >= 1000) {
              return `₦${(value / 1000).toFixed(1)}K`
            } else {
              return `₦${
                value?.toLocaleString('en-US', {
                  minimumFractionDigits: 0,
                  maximumFractionDigits: 0,
                }) || '0'
              }`
            }
          },
        },
        grid: {
          color: '#e0e0e0',
        },
      },
    },
  }

  return (
    <div className='flex h-full'>
      {data ? (
        <Bar
          datasetIdKey='money-bar-chart'
          data={processedChartData}
          options={chartOptions}
        />
      ) : null}
    </div>
  )
}

export default PayrollChart
